import { parseDate } from '@ja/core-ui/lib/date'

import type { TokenResponse } from '../types'

// Intentionally hardcoded: token requests run via raw fetch() before the Vue/Inertia
// app is initialized (the token lifecycle bootstraps the client connection itself),
// so Wayfinder-generated route functions — which require the app context — cannot be used.
const MINT_ENDPOINT = '/api/realtime/token'
const REFRESH_ENDPOINT = '/api/realtime/token/refresh'
const REFRESH_LEAD_SECONDS = 15
const SKEW_SECONDS = 5
// 503 from the token endpoint = transport intentionally unavailable
// (NullDriver / missing Centrifugo env). Latched for the page lifetime.
const TRANSPORT_UNAVAILABLE_STATUS = 503

type State = {
  token: TokenResponse | null
  expEpoch: number
  refreshTimer: ReturnType<typeof setTimeout> | null
  inflight: Promise<TokenResponse> | null
  connectionId: string
  listenersInstalled: boolean
  transportDisabled: boolean
}

const state: State = {
  token: null,
  expEpoch: 0,
  refreshTimer: null,
  inflight: null,
  connectionId: generateConnectionId(),
  listenersInstalled: false,
  transportDisabled: false,
}

export class RealtimeTransportDisabledError extends Error {
  constructor() {
    super('realtime transport unavailable')
    this.name = 'RealtimeTransportDisabledError'
  }
}

export function isRealtimeTransportDisabled(): boolean {
  return state.transportDisabled
}

function generateConnectionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'rt-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function clearRefreshTimer(): void {
  if (state.refreshTimer !== null) {
    clearTimeout(state.refreshTimer)
    state.refreshTimer = null
  }
}

function scheduleRefresh(): void {
  clearRefreshTimer()
  if (state.expEpoch === 0) return

  const nowEpoch = Math.floor(Date.now() / 1000)
  const delaySeconds = state.expEpoch - REFRESH_LEAD_SECONDS - nowEpoch

  // Timer-driven refreshes have no consumer awaiting them; a failure here must
  // not surface as an uncaught rejection. invalidate() already ran, so the
  // next consumer-driven getToken() re-mints and surfaces the error itself.
  if (delaySeconds <= 0) {
    refresh().catch(() => {})
    return
  }

  state.refreshTimer = setTimeout(() => {
    refresh().catch(() => {})
  }, delaySeconds * 1000)
}

function isStale(): boolean {
  if (state.expEpoch === 0) return true
  const nowEpoch = Math.floor(Date.now() / 1000)
  return state.expEpoch <= nowEpoch - SKEW_SECONDS
}

async function request(endpoint: string): Promise<TokenResponse> {
  if (state.transportDisabled) {
    throw new RealtimeTransportDisabledError()
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (response.status === TRANSPORT_UNAVAILABLE_STATUS) {
    state.transportDisabled = true
    clearRefreshTimer()
    throw new RealtimeTransportDisabledError()
  }

  if (!response.ok) {
    throw Object.assign(new Error('realtime token request failed'), {
      status: response.status,
    })
  }

  return (await response.json()) as TokenResponse
}

function storeToken(payload: TokenResponse): TokenResponse {
  state.token = payload
  state.expEpoch = Math.floor((parseDate(payload.expiresAt)?.getTime() ?? 0) / 1000)
  scheduleRefresh()
  return payload
}

async function mint(): Promise<TokenResponse> {
  if (state.inflight !== null) return state.inflight

  state.inflight = request(MINT_ENDPOINT)
    .then(storeToken)
    .finally(() => {
      state.inflight = null
    })

  return state.inflight
}

async function refresh(): Promise<TokenResponse> {
  if (state.inflight !== null) return state.inflight

  state.inflight = request(REFRESH_ENDPOINT)
    .then(storeToken)
    .catch((err: unknown) => {
      invalidate()
      throw err
    })
    .finally(() => {
      state.inflight = null
    })

  return state.inflight
}

export async function getToken(): Promise<TokenResponse> {
  installVisibilityListeners()

  if (state.token !== null && !isStale()) {
    return state.token
  }

  if (state.token !== null && isStale()) {
    invalidate()
  }

  return mint()
}

export function invalidate(): void {
  clearRefreshTimer()
  state.token = null
  state.expEpoch = 0
  state.connectionId = generateConnectionId()
}

export function getConnectionId(): string {
  return state.connectionId
}

function installVisibilityListeners(): void {
  if (state.listenersInstalled) return
  if (typeof document === 'undefined') return

  const onWake = (): void => {
    if (state.token !== null && isStale()) {
      invalidate()
    }
  }

  document.addEventListener('visibilitychange', onWake)
  window.addEventListener('pageshow', onWake)

  state.listenersInstalled = true
}

export function useRealtimeToken() {
  return {
    getToken,
    refresh,
    invalidate,
    getConnectionId,
  }
}
